import React, { useEffect, useRef, useState } from "react";
import InstagramGlyph from "./InstagramGlyph";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import BottomSheet from "./BottomSheet";
import CountryFlag from "./CountryFlag";
import CountrySheet from "./CountrySheet";
import ProfileMeta, { countryName } from "./ProfileMeta";
import FanAvatar from "./FanAvatar";
import FanClaimButtons from "./FanClaimButtons";
import ImageCropperModal from "./ImageCropperModal";
import { clearAppRole } from "../lib/appRole";
import { pickProfilePhoto } from "../lib/pickProfilePhoto";
import { dataUrlToAvatarBlob } from "../lib/fanPhotoUpload";
import {
  INSTAGRAM_KEY,
  NICKNAME_KEY,
  readIdentity,
  writeIdentity,
} from "../lib/fanProfile";
import {
  FanUser,
  fanFetchMe,
  fanPatchMe,
  fanSignOut,
  fanUploadAvatar,
  getFanUser,
  getIgDmVerifyCapable,
  getPhotosCapable,
  isFanSignedIn,
} from "../lib/fanAccount";
import IgConnectRow from "./IgConnectRow";
import VerifiedTick, { VerifiedTickTease } from "./VerifiedTick";
import { PENCIL } from "./FanProfileHero";
import "./FanProfileEditorSheet.css";
import { sanitizeInstagramHandle } from "../utils/instagramHandle";
import { t } from "../i18n";

// "My Profile" editor (locked r6 §2; founder decision: nickname and Instagram
// are two separate rows): big identity bubble, then a name row and an
// @instagram row that each AUTO-SAVE with the small green ✓ (no Save
// button — founder killed it), then CLAIM YOUR SPOT 🏆 straight into the
// three sign-in doors. Signed-in fans see "Signed in as … · Sign out"
// there instead. Photo tap-to-change appears only while the server photo
// pipeline is live.

const SAVE_DEBOUNCE_MS = 600;

const readCurrent = (): { name: string; ig: string } => {
  const account = getFanUser();
  // A nameless account (rare: merge hiccup, fresh magic-link account from
  // another device) falls back to the device identity — the next edit
  // PATCHes it up to the account, self-healing the gap.
  return {
    name: account?.name || readIdentity(NICKNAME_KEY),
    ig: account?.instagram || readIdentity(INSTAGRAM_KEY),
  };
};

const PERSON = (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
    <circle cx="12" cy="8" r="4.2" />
    <path d="M4 20.5c.6-4 3.9-6.3 8-6.3s7.4 2.3 8 6.3c.1.5-.3 1-.8 1H4.8c-.5 0-.9-.5-.8-1Z" />
  </svg>
);
const ROW_CHEVRON = (
  <svg className="fpe-row-chev" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m9 5 7 7-7 7" />
  </svg>
);

const FanProfileEditorSheet: React.FC<{
  open: boolean;
  onClose: () => void;
  /** Opened by the panel's little Verify: start the Instagram message right away. */
  connectOnOpen?: boolean;
}> = ({ open, onClose, connectOnOpen }) => {
  const [signedIn, setSignedIn] = useState(isFanSignedIn());
  const [name, setName] = useState("");
  const [ig, setIg] = useState("");
  const [savedName, setSavedName] = useState("");
  const [savedIg, setSavedIg] = useState("");
  const [saveError, setSaveError] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // What the debounced save should write — always the latest keystrokes.
  const latestRef = useRef({ name: "", ig: "" });

  // Photos (phase 2 of the gate): visible only when the server pipeline is
  // live (GET /me `photos` flag) AND the fan is signed in — photos hang on
  // the account, never the device.
  const [photosCapable, setPhotosCapable] = useState(getPhotosCapable());
  // "Connect Instagram" (1.5.2): while the server has "Verify with a DM" on,
  // Instagram is connected by a message (IgConnectRow), never typed.
  const [igDmCapable, setIgDmCapable] = useState(getIgDmVerifyCapable());
  // Which row is being typed in (Edit Profile, founder 2026-11-02): each row
  // shows its value and an arrow until it's tapped.
  const [editing, setEditing] = useState<"name" | "ig" | null>(null);
  const [countryOpen, setCountryOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const v = readCurrent();
    setSignedIn(isFanSignedIn());
    setName(v.name);
    setIg(v.ig);
    setSavedName(v.name);
    setSavedIg(v.ig);
    latestRef.current = v;
    setSaveError(false);
    setAvatarError("");
    setPhotosCapable(getPhotosCapable());
    // Fresh capability + account state on open (cheap; also self-heals a
    // stale avatar after an admin pull).
    if (isFanSignedIn()) {
      fanFetchMe()
        .then((me) => {
          setPhotosCapable(!!me.photos);
          setIgDmCapable(!!me.igDmVerify);
        })
        .catch(() => {});
    }
    setEditing(null);
  }, [open]);

  const save = async (v: { name: string; ig: string }) => {
    const nick = v.name.trim().slice(0, 50);
    const handle = sanitizeInstagramHandle(v.ig);
    // Device storage always mirrors the latest identity, signed in or not —
    // it keeps the signed-out fallback fresh and fires the chip's event.
    writeIdentity(NICKNAME_KEY, nick);
    writeIdentity(INSTAGRAM_KEY, handle);
    if (isFanSignedIn()) {
      await fanPatchMe({ name: nick, instagram: handle });
    }
  };

  const scheduleSave = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      timerRef.current = null;
      const v = { ...latestRef.current };
      try {
        await save(v);
        setSavedName(v.name);
        setSavedIg(v.ig);
        setSaveError(false);
      } catch {
        setSaveError(true);
      }
    }, SAVE_DEBOUNCE_MS);
  };

  // Closing mid-debounce must not lose the keystrokes — flush immediately.
  useEffect(() => {
    if (open) return;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, [open]);
  const flushAndClose = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
      save(latestRef.current).catch(() => {});
    }
    onClose();
  };

  const handleSignedIn = (fan: FanUser) => {
    setSignedIn(true);
    setPhotosCapable(getPhotosCapable());
    const v = { name: fan.name || "", ig: fan.instagram || "" };
    setName(v.name);
    setIg(v.ig);
    setSavedName(v.name);
    setSavedIg(v.ig);
    latestRef.current = v;
  };

  const handleSignOut = async () => {
    if (isFanSignedIn()) await fanSignOut();
    // In the app, Sign out is also the door out of fan mode (replaces the
    // old Browse "Log out", founder decision): back to the welcome tour, where
    // Get Started re-asks the role. Device history survives either way.
    if (Capacitor.isNativePlatform()) {
      clearAppRole();
      onClose();
      navigate("/welcome");
      return;
    }
    setSignedIn(false);
    const v = readCurrent();
    setName(v.name);
    setIg(v.ig);
    setSavedName(v.name);
    setSavedIg(v.ig);
    latestRef.current = v;
  };

  const handleAvatarTap = async () => {
    // Identity rework: the editor is a signed-in surface — signed-out
    // fans reach the sign-in sheet instead (chip / My Profile rule).
    if (avatarBusy || !signedIn || !photosCapable) return;
    const dataUrl = await pickProfilePhoto();
    if (dataUrl) setCropSrc(dataUrl);
  };

  const handleCropConfirm = async (croppedDataUrl: string) => {
    setCropSrc(null);
    setAvatarBusy(true);
    setAvatarError("");
    try {
      const blob = await dataUrlToAvatarBlob(croppedDataUrl);
      await fanUploadAvatar(blob);
    } catch (err: any) {
      const msg = err?.response?.data?.msg;
      setAvatarError(
        msg === "photo_rejected"
          ? t("fanProfileEditorSheet.thatPhotoDidnT")
          : msg === "file_too_large"
          ? t("fanProfileEditorSheet.thatPhotoIsToo")
          : t("fanProfileEditorSheet.couldnTUploadTry")
      );
    } finally {
      setAvatarBusy(false);
    }
  };

  if (!open) return null;

  const account = getFanUser();
  const bubbleName = name.trim() || (ig ? `@${ig}` : "");
  const verifiedHandle = !!account?.instagramVerified && !!account.instagram;
  const nameSaved = name === savedName && name.trim() !== "";
  const igSaved = ig === savedIg && ig.trim() !== "";

  // Signed-out taps are live too — they reveal the sign-in doors below.
  const canTapPhoto = !avatarBusy && (!signedIn || photosCapable);

  return (
    <BottomSheet open={open} title={t("fanProfileEditorSheet.editProfile")} onClose={flushAndClose}>
      {/* Photo tap-to-change — NO camera icon anywhere (locked r6). */}
      <button
        type="button"
        className={`fpe-ava-tap${avatarBusy ? " fpe-ava-tap--busy" : ""}`}
        onClick={handleAvatarTap}
        disabled={!canTapPhoto}
        aria-label={account?.avatarUrl ? t("fanProfileEditorSheet.changeYourPhoto") : t("fanProfileEditorSheet.addYourPhoto")}
      >
        <FanAvatar
          className="fps-ava-big"
          name={bubbleName}
          avatarUrl={account?.avatarUrl}
          showBadge={!signedIn}
          silhouetteSize={36}
        />
        {signedIn && photosCapable && (
          <span className="fph-pencil fpe-pencil" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d={PENCIL} />
            </svg>
          </span>
        )}
      </button>
      {signedIn && bubbleName && (
        <div className="fpe-ava-name">
          <span className="fpe-ava-name-text">{bubbleName}</span>
          {verifiedHandle && <VerifiedTick className="fpe-ava-tick" label={t("fanConnect.verified")} />}
        </div>
      )}
      {signedIn && ig && bubbleName !== `@${ig}` && <div className="fpe-ava-handle">@{ig}</div>}
      {signedIn && <ProfileMeta countryCode={account?.countryCode} joinedAt={account?.joinedAt} />}
      {avatarBusy && <div className="fpe-ava-cap">{t("fanProfileEditorSheet.uploading")}</div>}
      {avatarError && <div className="fpe-error">{avatarError}</div>}

      <div className="fpe-section">{t("fanProfileEditorSheet.profileInfo")}</div>
      {editing === "name" ? (
        <div className="fpe-row fpe-row--edit">
          <span className="fpe-row-ic fpe-row-ic--person">{PERSON}</span>
          <label className="fpe-row-text">
            <span className="fpe-row-label">{t("step2Identity.displayName")}</span>
            <input
              id="fpe-nick"
              className="fpe-row-input"
              type="text"
              autoFocus
              placeholder={t("fanProfileEditorSheet.yourName")}
              value={name}
              maxLength={50}
              autoCapitalize="words"
              onBlur={() => setEditing(null)}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
              }}
              onChange={(e) => {
                setName(e.target.value);
                latestRef.current = { ...latestRef.current, name: e.target.value };
                setSaveError(false);
                scheduleSave();
              }}
            />
          </label>
          {nameSaved && !saveError && (
            <span className="fpe-check" aria-label={t("fanProfileEditorSheet.saved")}>✓</span>
          )}
        </div>
      ) : (
        <button type="button" className="fpe-row fpe-row--name" onClick={() => setEditing("name")}>
          <span className="fpe-row-ic fpe-row-ic--person">{PERSON}</span>
          <span className="fpe-row-text">
            <span className="fpe-row-label">{t("step2Identity.displayName")}</span>
            <span className={`fpe-row-value${name.trim() ? "" : " fpe-row-value--ask"}`}>{name.trim() || t("fanProfileEditorSheet.yourName")}</span>
          </span>
          {ROW_CHEVRON}
        </button>
      )}
      {signedIn && igDmCapable ? (
        <IgConnectRow
          startNow={connectOnOpen}
          connectedAs={account?.instagramVerified && account.instagram ? account.instagram : null}
          onConnected={(username) => {
            // Instagram's handle is the handle now: a save still waiting with
            // an older one must not land after it.
            if (timerRef.current) {
              clearTimeout(timerRef.current);
              timerRef.current = null;
            }
            writeIdentity(INSTAGRAM_KEY, username);
            setIg(username);
            setSavedIg(username);
            latestRef.current = { ...latestRef.current, ig: username };
            setSaveError(false);
          }}
          onRemove={async () => {
            // A name save still waiting carries the old handle: it must not
            // land after the removal and put it back as typed text. Hold it,
            // remove, then let it go with no handle.
            const pending = !!timerRef.current;
            if (timerRef.current) {
              clearTimeout(timerRef.current);
              timerRef.current = null;
            }
            try {
              // An empty handle ends the verification too (server PATCH /fans/me).
              await fanPatchMe({ instagram: "" });
              // The device copy goes as well: a later sign-in here would
              // otherwise hand it back to the account (mergeDeviceIntoAccount).
              writeIdentity(INSTAGRAM_KEY, "");
              setIg("");
              setSavedIg("");
              latestRef.current = { ...latestRef.current, ig: "" };
              setSaveError(false);
            } finally {
              if (pending) scheduleSave();
            }
          }}
        />
      ) : editing === "ig" ? (
        <div className="fpe-row fpe-row--edit">
          <span className="fpe-row-ic"><InstagramGlyph size={26} /></span>
          <label className="fpe-row-text">
            <span className="fpe-row-label">{t("fanProfileEditorSheet.instagramOptional")}{!ig && <VerifiedTickTease />}</span>
            <input
              id="fpe-ig"
              className="fpe-row-input"
              type="text"
              autoFocus
              placeholder={t("fanProfileEditorSheet.yourhandle")}
              value={ig ? `@${ig}` : ""}
              maxLength={31}
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              onBlur={() => setEditing(null)}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
              }}
              onChange={(e) => {
                const handle = sanitizeInstagramHandle(e.target.value);
                setIg(handle);
                latestRef.current = { ...latestRef.current, ig: handle };
                setSaveError(false);
                scheduleSave();
              }}
            />
          </label>
          {igSaved && !saveError && (
            <span className="fpe-check" aria-label={t("fanProfileEditorSheet.saved")}>✓</span>
          )}
        </div>
      ) : (
        <button type="button" className="fpe-row fpe-row--ig" onClick={() => setEditing("ig")}>
          <span className="fpe-row-ic"><InstagramGlyph size={26} /></span>
          <span className="fpe-row-text">
            <span className="fpe-row-label">{t("fanProfileEditorSheet.instagramOptional")}{!ig && <VerifiedTickTease />}</span>
            <span className={`fpe-row-value${ig ? "" : " fpe-row-value--ask"}`}>{ig ? `@${ig}` : t("fanProfileEditorSheet.yourhandle")}</span>
          </span>
          {ROW_CHEVRON}
        </button>
      )}
      {signedIn && (
        <button type="button" className="fpe-row fpe-row--country" onClick={() => setCountryOpen(true)}>
          <span className="fpe-row-ic"><CountryFlag code={account?.countryCode} /></span>
          <span className="fpe-row-text">
            <span className="fpe-row-label">{t("profileMeta.country")}</span>
            <span className={`fpe-row-value${account?.countryCode ? "" : " fpe-row-value--ask"}`}>
              {account?.countryCode ? countryName(account.countryCode) : t("profileMeta.addCountry")}
            </span>
          </span>
          {ROW_CHEVRON}
        </button>
      )}
      <CountrySheet
        open={open && countryOpen}
        selectedCode={account?.countryCode}
        onPick={(code) => {
          setCountryOpen(false);
          fanPatchMe({ countryCode: code }).then(() => setSaveError(false)).catch(() => setSaveError(true));
        }}
        onClose={() => setCountryOpen(false)}
      />
      {saveError && <div className="fpe-error">{t("fanProfileEditorSheet.couldnTSaveCheck")}</div>}

      {signedIn ? (
        <div className="fpe-signedin">
          {t("fanProfileEditorSheet.signedIn")}{account?.email ? t("fanProfileEditorSheet.as", { email: account.email }) : ""} ·{" "}
          <button type="button" className="fpe-signout" onClick={handleSignOut}>
            {t("fanProfileEditorSheet.signOut")}
          </button>
        </div>
      ) : (
        // Session expired while the sheet was open (the only signed-out way
        // in) — degrade to the doors instead of a dead editor.
        <>
          <div className="fpe-photoask">{t("fanProfileEditorSheet.signInToEdit")}</div>
          <FanClaimButtons compact onSignedIn={handleSignedIn} />
        </>
      )}

      {cropSrc && (
        <ImageCropperModal
          imageSrc={cropSrc}
          cropShape="round"
          aspect={1}
          confirmLabel={t("fanProfileEditorSheet.useThisPhoto")}
          onCancel={() => setCropSrc(null)}
          onConfirm={handleCropConfirm}
        />
      )}
    </BottomSheet>
  );
};

export default FanProfileEditorSheet;
